import {
  pictureCharacterMatchLevels,
  soundBubbleLevels,
  storySequenceLevels,
  type PictureCharacterMatchPayload,
  type SoundBubblePayload,
  type StorySequencePayload
} from "@/content/chinese";
import type { LevelDefinition } from "@/domain";

export const CHINESE_PLAYER_ACTIVITY_TYPES = [
  "sound-bubbles",
  "picture-character-match",
  "story-sequence"
] as const;

export type ChinesePlayerActivityType =
  (typeof CHINESE_PLAYER_ACTIVITY_TYPES)[number];

export type NarratedChineseLevel<
  TPayload,
  TActivityType extends ChinesePlayerActivityType
> = Omit<LevelDefinition<TPayload>, "activityType" | "promptAudio"> & {
  readonly activityType: TActivityType;
  readonly promptAudio: string;
};

export type SoundBubbleLevel = NarratedChineseLevel<
  SoundBubblePayload,
  "sound-bubbles"
>;

export type PictureCharacterMatchLevel = NarratedChineseLevel<
  PictureCharacterMatchPayload,
  "picture-character-match"
>;

export type StorySequenceLevel = NarratedChineseLevel<
  StorySequencePayload,
  "story-sequence"
>;

export type ChinesePlayerLevel =
  | SoundBubbleLevel
  | PictureCharacterMatchLevel
  | StorySequenceLevel;

function isNarratedLevel<
  TPayload,
  TActivityType extends ChinesePlayerActivityType
>(
  level: LevelDefinition<TPayload>,
  activityType: TActivityType
): level is NarratedChineseLevel<TPayload, TActivityType> {
  return (
    level.domain === "chinese" &&
    level.activityType === activityType &&
    typeof level.promptAudio === "string" &&
    level.promptAudio.trim().length > 0
  );
}

function isSoundBubbleLevel(
  level: LevelDefinition<SoundBubblePayload>
): level is SoundBubbleLevel {
  return isNarratedLevel(level, "sound-bubbles");
}

function isPictureCharacterMatchLevel(
  level: LevelDefinition<PictureCharacterMatchPayload>
): level is PictureCharacterMatchLevel {
  return isNarratedLevel(level, "picture-character-match");
}

function isStorySequenceLevel(
  level: LevelDefinition<StorySequencePayload>
): level is StorySequenceLevel {
  return isNarratedLevel(level, "story-sequence");
}

export const chinesePlayerLevels: readonly ChinesePlayerLevel[] = [
  ...soundBubbleLevels.filter(isSoundBubbleLevel),
  ...pictureCharacterMatchLevels.filter(isPictureCharacterMatchLevel),
  ...storySequenceLevels.filter(isStorySequenceLevel)
];

export function getChinesePlayerLevel(
  levelId: string
): ChinesePlayerLevel | undefined {
  return chinesePlayerLevels.find(({ id }) => id === levelId);
}

export function getChineseNarrationCaption(
  level: ChinesePlayerLevel
): string {
  switch (level.activityType) {
    case "sound-bubbles":
      return level.payload.spokenText;
    case "picture-character-match":
      return level.instruction;
    case "story-sequence":
      return level.payload.narration;
  }
}
